import React, { useState, useEffect } from 'react';
import { promotionGroups as promotionGroupService } from '../services/promotionGroups';
import PromotionGroupForm from '../components/PromotionGroupForm';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const PromotionGroups = ({ user }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [error, setError] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await promotionGroupService.getAll();
      console.log('Promotion Groups API Response:', res);
      if (res.data && res.data.length > 0 && res.data[0].groupDetails) {
        console.log('Sample Detail:', res.data[0].groupDetails[0]);
      }
      setGroups(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch promotion groups', err);
      setError(err.response?.data?.message || err.message || 'Gagal memuat data');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const getImageUrl = (detail) => {
    if (!detail) {
      console.log('getImageUrl: detail is empty');
      return null;
    }
    
    console.log('getImageUrl - Detail:', detail);
    
    // API now returns image_url directly with the full path
    if (detail.image_url) {
      // If it starts with /, it's a relative path - prepend API_BASE_URL
      if (detail.image_url.startsWith('/')) {
        const fullUrl = `${API_BASE_URL}${detail.image_url}`;
        console.log('Final image_url (from image_url field, relative):', fullUrl);
        return fullUrl;
      } else {
        console.log('Final image_url (from image_url field, absolute):', detail.image_url);
        return detail.image_url;
      }
    }
    
    // Fallback to image_path if image_url is not available
    if (detail.image_path) {
      // image_path might be just the filename or a path
      let url;
      if (detail.image_path.startsWith('/')) {
        url = `${API_BASE_URL}/storage${detail.image_path}`;
      } else if (detail.image_path.startsWith('http')) {
        url = detail.image_path;
      } else {
        url = `${API_BASE_URL}/storage/promotion-images/${detail.image_path}`;
      }
      console.log('Final image_url (from image_path field):', url);
      return url;
    }
    
    console.log('getImageUrl: No image found for detail');
    return null;
  };

  const handleOpenForm = (group = null) => {
    setEditGroup(group);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditGroup(null);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editGroup?.id) {
        await promotionGroupService.update(editGroup.id, formData);
      } else {
        await promotionGroupService.create(formData);
      }
      fetchGroups();
      handleFormClose();
    } catch (err) {
      console.error('Failed to save promotion group', err);
      alert(err.response?.data?.message || 'Gagal menyimpan data');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus grup promosi ini? Detail yang terkait juga akan terhapus.')) return;
    try {
      await promotionGroupService.delete(id);
      fetchGroups();
    } catch (err) {
      alert(err.response?.data?.message || 'Gagal menghapus data');
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Memuat data...</div>;
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Master Grup Promosi</h2>
          <p className="text-gray-500 text-sm mt-1">Kelola grup promosi dan detail produk promosi dengan foto dan warna.</p>
        </div>
        <button
          onClick={() => handleOpenForm()}
          className="bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-blue-700 transition shadow-sm"
        >
          + Tambah Grup
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium">
          {error}
        </div>
      )}

      {/* List */}
      {groups.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
          <p className="text-gray-600 font-medium">Belum ada grup promosi.</p>
          <p className="text-gray-500 text-sm mt-1">Mulai dengan membuat grup promosi baru.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => {
            const details = group.groupDetails || group.group_details || [];
            const isExpanded = expandedGroup === group.id;
            
            return (
              <div key={group.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Group Header */}
                <div
                  onClick={() => setExpandedGroup(isExpanded ? null : group.id)}
                  className="cursor-pointer hover:bg-gray-50 transition p-5 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-gray-900">{group.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{details.length} detail produk</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                </div>

                {/* Group Details */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 p-5 space-y-4">
                    {details.length > 0 ? (
                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {details.map((detail, idx) => {
                          const imageUrl = getImageUrl(detail);
                          return (
                            <div key={detail.id || idx} className="bg-white rounded-lg overflow-hidden border border-gray-200">
                              {/* Image */}
                              <div className="relative h-36 bg-gray-100 overflow-hidden">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={detail.bike_name}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                                    onError={(e) => {
                                      console.error('Image failed to load:', imageUrl);
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              {/* Content */}
                              <div className="p-3">
                                <p className="font-semibold text-sm text-gray-900">{detail.bike_name || 'Produk tanpa nama'}</p>
                                <p className="text-xs text-gray-500 mt-2">
                                  <span className="font-medium">Warna:</span> {(detail.colors || []).length > 0 ? (detail.colors || []).join(', ') : 'Tidak ada'}
                                </p>
                                {(detail.colors || []).length > 0 && (
                                  <div className="flex gap-2 mt-3">
                                    {(detail.colors || []).slice(0, 5).map((color, i) => (
                                      <div
                                        key={i}
                                        className="w-5 h-5 rounded border border-gray-300"
                                        style={{ backgroundColor: color }}
                                        title={color}
                                      />
                                    ))}
                                    {(detail.colors || []).length > 5 && (
                                      <span className="text-xs text-gray-500 self-center">+{(detail.colors || []).length - 5}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 py-4">Tidak ada detail produk dalam grup ini.</p>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="border-t border-gray-100 bg-gray-50 px-5 py-3 flex justify-end gap-2">
                  <button
                    onClick={() => handleOpenForm(group)}
                    className="text-blue-600 hover:text-blue-900 bg-blue-50 px-3 py-1.5 rounded-lg text-sm font-medium transition"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(group.id)}
                    className="text-red-600 hover:text-red-900 bg-red-50 px-3 py-1.5 rounded-lg text-sm font-medium transition"
                  >
                    Hapus
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <PromotionGroupForm
          group={editGroup}
          onSubmit={handleFormSubmit}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
};

export default PromotionGroups;
